import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConnectorsModal } from './ConnectorsModalProvider';
import ConnectorsOverviewModal from './ConnectorsOverviewModal';
import ManageConnectionPanel from './ManageConnectionPanel';
import { connectorsAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

/**
 * ConnectorsModalHost — bridge between the URL-state provider and the
 * actual modal components.
 *
 * Mounted once at the App root inside ConnectorsModalProvider. Reads
 * `view` from context, renders the right modal. Owns the disconnect /
 * breadcrumb-back / panel-feedback state for the manage panel because
 * those are panel-lifecycle concerns the provider shouldn't care about.
 *
 *   view === 'overview' → <ConnectorsOverviewModal/>
 *   view === 'manage'   → <ManageConnectionPanel/>     (replaces overview)
 *   view === 'closed'   → renders nothing
 */
const ConnectorsModalHost: React.FC = () => {
  const {
    view,
    connectorId,
    registry,
    connectionByConnectorId,
    close,
    open: openOverview,
    refresh,
    connectResult,
  } = useConnectorsModal();
  const { success, error: notifyError } = useNotification();
  const [, setSearchParams] = useSearchParams();

  // Per-disconnect feedback banner, plus the in-flight flag the panel
  // reads. These are local to a single open-manage lifecycle, not
  // shared state — keeping them here avoids polluting the provider.
  const [disconnecting, setDisconnecting] = useState(false);
  const [panelFeedback, setPanelFeedback] = useState<
    { kind: 'info' | 'warn'; text: string } | null
  >(null);

  const managingEntry = view === 'manage' && connectorId
    ? registry.find((e) => e.id === connectorId) || null
    : null;
  const managingConnection = connectorId
    ? connectionByConnectorId[connectorId] || null
    : null;

  const handleBackToOverview = useCallback(() => {
    setPanelFeedback(null);
    // Clear just `connector`, keep `connectors` so we stay in overview.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('connector');
      next.set('connectors', '');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const handleCloseManage = useCallback(() => {
    setPanelFeedback(null);
    close();
  }, [close]);

  const handleDisconnect = useCallback(async () => {
    if (!managingEntry) return;
    setDisconnecting(true);
    try {
      // Wave 3 of the HeyGen migration: there is no longer a generic
      // disconnect endpoint. All v1 connectors are universal-key — disable
      // is the equivalent flip-off, and preserves the audit row.
      await connectorsAPI.disable(managingEntry.id);
      success('Disconnected.');
      await refresh();
      handleBackToOverview();
    } catch {
      notifyError("Couldn't disconnect. Try again.");
    } finally {
      setDisconnecting(false);
    }
  }, [managingEntry, refresh, success, notifyError, handleBackToOverview]);

  // Open by URL but registry not loaded yet — render nothing rather than
  // an inconsistent half-modal. The provider's initial fetch resolves on
  // mount; the user sees this for ~200ms tops.
  if (view === 'overview') {
    return <ConnectorsOverviewModal />;
  }
  if (view === 'manage' && managingEntry) {
    return (
      <ManageConnectionPanel
        entry={managingEntry}
        connection={managingConnection}
        onClose={handleCloseManage}
        onBack={handleBackToOverview}
        onDisconnect={handleDisconnect}
        isDisconnecting={disconnecting}
        feedback={panelFeedback}
        connectResult={connectResult}
        onConnectionChange={refresh}
      />
    );
  }
  // view === 'manage' but the requested connector isn't in the registry
  // yet (unknown id, or registry still loading) — fall back to overview
  // so deep-links never strand the user on a blank screen.
  if (view === 'manage' && !managingEntry && registry.length > 0) {
    // Unknown connector id. Quietly redirect to overview.
    openOverview();
    return null;
  }
  return null;
};

export default ConnectorsModalHost;
